/**
 * Job Subscription Plans Page
 * Shows subscription plans for candidates and employers
 */
import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Check,
  Crown,
  Zap,
  Star,
  ChevronRight,
  Loader2,
  Target,
  Building2,
  Sparkles,
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Plan Card Colors
const PLAN_COLORS = {
  gray: { card: 'border-gray-200', badge: 'bg-gray-100 text-gray-700', button: 'bg-gray-500 hover:bg-gray-600' },
  blue: { card: 'border-blue-500 shadow-blue-100', badge: 'bg-blue-100 text-blue-700', button: 'bg-blue-600 hover:bg-blue-700' },
  purple: { card: 'border-purple-500 shadow-purple-100', badge: 'bg-purple-100 text-purple-700', button: 'bg-purple-600 hover:bg-purple-700' },
  emerald: { card: 'border-emerald-500 shadow-emerald-100', badge: 'bg-emerald-100 text-emerald-700', button: 'bg-emerald-600 hover:bg-emerald-700' },
  amber: { card: 'border-amber-500 shadow-amber-100', badge: 'bg-amber-100 text-amber-700', button: 'bg-amber-600 hover:bg-amber-700' },
};

// Plan Card Component
const PlanCard = ({ plan, currency, onSelect, isCurrentPlan, loading }) => {
  const colors = PLAN_COLORS[plan.color] || PLAN_COLORS.blue;
  const isPopular = plan.badge === 'Most Popular' || plan.badge === 'Popular' || plan.badge === 'Best Value';
  const isFree = plan.billing_period === 'free';
  
  return (
    <Card className={`relative ${colors.card} border-2 ${isPopular ? 'shadow-lg scale-105' : ''}`}>
      {isPopular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <Badge className={`${colors.badge} px-3 py-1`}>
            <Star className="h-3 w-3 mr-1 fill-current" />
            {plan.badge}
          </Badge>
        </div>
      )}
      
      <CardHeader className="text-center pt-8">
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <CardDescription className="mt-2">{plan.description}</CardDescription>
        <div className="mt-4">
          <span className="text-4xl font-bold">
            {isFree ? 'Free' : `${currency.symbol}${plan.price}`}
          </span>
          {!isFree && (
            <span className="text-gray-500">/{plan.billing_period}</span>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {plan.features.map((feature, idx) => (
          <div key={idx} className="flex items-start gap-2">
            <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-600">{feature}</span>
          </div>
        ))}
      </CardContent>
      
      <CardFooter className="pt-4">
        <Button 
          className={`w-full ${colors.button}`}
          disabled={isCurrentPlan || loading}
          onClick={() => onSelect(plan)}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : isCurrentPlan ? (
            'Current Plan'
          ) : isFree ? (
            'Get Started'
          ) : (
            <>
              <Crown className="h-4 w-4 mr-2" />
              {plan.badge === 'Enterprise' ? 'Contact Sales' : 'Subscribe'}
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

// Main Page Component
const SubscriptionPlansPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { country, formatCurrency } = useCountry();
  const { toast } = useToast();
  
  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAuthenticated = !!localStorage.getItem('token');
  
  // Determine initial tab from URL
  const isEmployer = location.pathname.includes('employer');
  const [activeTab, setActiveTab] = useState(isEmployer ? 'employer' : 'candidate');
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribing, setSubscribing] = useState(false);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [currency, setCurrency] = useState({ symbol: 'Rs', code: 'LKR' });

  useEffect(() => {
    fetchPlans();
    if (isAuthenticated && user?.id) {
      fetchCurrentSubscription();
    }
  }, [activeTab, country, isAuthenticated, user]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/api/job-dashboard/subscriptions/plans?plan_type=${activeTab}&country=${country}`
      );
      
      if (response.data.success) {
        setPlans(response.data.plans || []);
        setCurrency(response.data.currency);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentSubscription = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/job-dashboard/subscriptions/${user.id}`
      );
      
      if (response.data.success && response.data.subscription) {
        setCurrentSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleSelectPlan = async (plan) => {
    if (!isAuthenticated) {
      toast({
        title: 'Sign in required',
        description: 'Please sign in to subscribe to a plan',
      });
      navigate('/auth');
      return;
    }

    if (plan.billing_period === 'free') {
      toast({
        title: 'Free plan activated',
        description: 'You can start using the free features now',
      });
      navigate(activeTab === 'employer' ? '/jobs/employer-dashboard' : '/jobs/dashboard');
      return;
    }

    if (plan.badge === 'Enterprise') {
      toast({
        title: 'Contact Sales',
        description: 'Our team will reach out to discuss enterprise options',
      });
      return;
    }

    try {
      setSubscribing(true);
      
      // Generate subscription ID
      const subscriptionId = `SUB-${Date.now()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
      
      // For Sri Lanka (LKR), use PayHere
      if (country === 'SL' || currency.code === 'LKR') {
        // Check if PayHere is configured
        const configRes = await axios.get(`${API_URL}/api/payments/payhere/config`);
        
        if (!configRes.data?.config?.merchant_id) {
          // PayHere not configured - fall back to mock subscription
          toast({
            title: 'Payment gateway not configured',
            description: 'Using demo mode. Contact admin to enable PayHere payments.',
            variant: 'warning',
          });
          
          // Create mock subscription
          const response = await axios.post(
            `${API_URL}/api/job-dashboard/subscriptions/subscribe?country=${country}`,
            {
              plan_id: plan.id,
              user_id: user.id,
              user_type: activeTab,
            }
          );
          
          if (response.data.success) {
            toast({
              title: 'Subscription activated (Demo)',
              description: `You're now subscribed to ${plan.name}`,
            });
            navigate(activeTab === 'employer' ? '/jobs/employer-dashboard' : '/jobs/dashboard');
          }
          return;
        }
        
        // Initiate PayHere subscription payment
        const paymentRes = await axios.post(`${API_URL}/api/payments/payhere/subscription/initiate`, {
          subscription_id: subscriptionId,
          plan_id: plan.id,
          plan_name: plan.name,
          amount: plan.price,
          currency: 'LKR',
          recurrence: '1 Month',
          duration: 'Forever',
          first_name: user.name?.split(' ')[0] || 'Customer',
          last_name: user.name?.split(' ').slice(1).join(' ') || '',
          email: user.email,
          phone: user.phone || '0771234567',
          user_id: user.id,
          user_type: activeTab,
        });
        
        if (paymentRes.data.success) {
          // Create hidden form and submit to PayHere
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = paymentRes.data.checkout_url;
          
          Object.entries(paymentRes.data.payment_data).forEach(([key, value]) => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = key;
            input.value = value;
            form.appendChild(input);
          });
          
          document.body.appendChild(form);
          form.submit();
          return;
        }
      } else {
        // For UK (GBP), create subscription directly (Stripe would go here)
        const response = await axios.post(
          `${API_URL}/api/job-dashboard/subscriptions/subscribe?country=${country}`,
          {
            plan_id: plan.id,
            user_id: user.id,
            user_type: activeTab,
          }
        );
        
        if (response.data.success) {
          toast({
            title: 'Subscription activated!',
            description: `You're now subscribed to ${plan.name}`,
          });
          navigate(activeTab === 'employer' ? '/jobs/employer-dashboard' : '/jobs/dashboard');
        }
      }
    } catch (error) {
      console.error('Subscription error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to process subscription',
        variant: 'destructive',
      });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="subscription-plans-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 text-blue-200 text-sm mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/jobs" className="hover:text-white">Jobs</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-white">Plans</span>
          </div>
          
          <h1 className="text-4xl font-bold mb-4">
            <Sparkles className="inline h-10 w-10 mr-2" />
            Choose Your Plan
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Unlock powerful features to accelerate your {activeTab === 'employer' ? 'hiring' : 'job search'}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
            <TabsTrigger value="candidate" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Job Seekers
            </TabsTrigger>
            <TabsTrigger value="employer" className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Employers
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Plans Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-start">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                currency={currency}
                onSelect={handleSelectPlan}
                isCurrentPlan={currentSubscription?.plan_id === plan.id}
                loading={subscribing}
              />
            ))}
          </div>
        )}

        {/* FAQ / Info Section */}
        <div className="mt-16 text-center">
          <h3 className="text-xl font-semibold mb-4">Questions?</h3>
          <p className="text-gray-600 mb-4">
            All plans include a 7-day free trial. Cancel anytime.
          </p>
          <Button variant="outline" onClick={() => navigate('/contact')}>
            Contact Support
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlansPage;
